// ============================================================================
// ChatInput — bottom-fixed text input + Web Speech mic + send.
// Mic is hidden entirely if the browser exposes neither SpeechRecognition
// variant (Firefox fallback).
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Send } from 'lucide-react';

// deno-lint-ignore no-explicit-any
const SpeechRecognitionImpl: any =
  typeof window !== 'undefined'
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).SpeechRecognition ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).webkitSpeechRecognition
    : null;

interface ChatInputProps {
  /** A turn is on its way. Only Send waits; the box stays usable so the
   *  keyboard does not drop and nothing typed is lost. */
  sending: boolean;
  onSend: (text: string) => void;
  /** Words to put in the box: a failed turn's message, or an example picked
   *  on the empty screen. `n` changes each time, so the same words can come twice. */
  draft?: { text: string; n: number } | null;
  /** A sentence carried in from the Ask AI bar on Home (item #650). Placed in the box, never sent for you. */
  initialText?: string;
}

export function ChatInput({ sending, onSend, initialText, draft }: ChatInputProps) {
  const [text, setText] = useState(initialText ?? '');
  const boxRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!draft) return;
    setText(draft.text);
    if (draft.text) boxRef.current?.focus();
  }, [draft]);
  const [listening, setListening] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recRef = useRef<any>(null);
  const silenceTimerRef = useRef<number | null>(null);

  const supportsVoice = !!SpeechRecognitionImpl;

  useEffect(() => {
    return () => {
      if (recRef.current) {
        try {
          recRef.current.stop();
        } catch {
          /* noop */
        }
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, []);

  const startListening = () => {
    if (!supportsVoice) return;
    const rec = new SpeechRecognitionImpl();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event: { resultIndex: number; results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>; }) => {
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        finalChunk += event.results[i][0].transcript;
      }
      setText((prev) => {
        // Replace last appended interim chunk; for simplicity, append finals
        // only and ignore interim drift. Most users get the gist.
        const final = event.results[event.results.length - 1].isFinal;
        if (final) {
          return (prev + ' ' + finalChunk).trim();
        }
        return prev;
      });

      // Reset 2-sec silence timer on each result
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = window.setTimeout(() => {
        rec.stop();
      }, 2000);
    };

    rec.onend = () => {
      setListening(false);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };

    rec.onerror = () => {
      setListening(false);
    };

    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recRef.current) {
      try {
        recRef.current.stop();
      } catch {
        /* noop */
      }
    }
    setListening(false);
  };

  const handleSend = () => {
    if (!text.trim() || sending) return;
    onSend(text);
    setText('');
  };

  return (
    <div className="border-t border-tcs-border bg-tcs-bg-muted px-3 py-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={boxRef}
          aria-label="Message Ask AI"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={1}
          // CW-I Phase 4. Was "Tell me what's going on at your facilities…",
          // which describes a feedback intake box — so that is what people
          // used it as. The copilot carries ~28 intents.
          placeholder="Ask, log, or look something up…"
          className="min-h-[44px] w-full resize-none rounded-lg border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        {supportsVoice && (
          <button
            type="button"
            onClick={listening ? stopListening : startListening}
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
              listening
                ? 'animate-pulse bg-st-danger-solid text-st-solid-ink'
                : 'bg-tcs-surface-strong text-tcs-text-secondary hover:bg-tcs-border-strong'
            } disabled:opacity-60`}
            aria-label={listening ? 'Stop voice input' : 'Start voice input'}
          >
            {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>
        )}

        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tcs-inverse text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
          aria-label="Send"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
